<script setup>
import { computed, onBeforeUnmount, watch } from 'vue'
import { RouterView, useRoute } from 'vue-router'
import { Radio, Wifi } from 'lucide-vue-next'
import { useDemoStore } from './demo/store.js'
import { useI18n } from './i18n.js'
import { applySessionTheme, applyBrandingStyles } from './theme/sessionTheme.js'
import { applyBrandPalette } from './branding/brandPalette.js'
import EventBrand from './components/EventBrand.vue'
import { submitFormOnShortcut } from './forms/submitShortcut.js'

const route = useRoute()
const store = useDemoStore()
const { state: language, t, syncLocale } = useI18n()
const demoMode = import.meta.env.VITE_DEMO_MODE === 'true'

const audienceSurface = computed(() => Boolean(route.params.sessionCode))
const bare = computed(() => route.meta.displaySurface || route.path === '/join' || route.path.startsWith('/legal/') || route.query.embed === '1' || (audienceSurface.value && (route.query.ht === 'true' || !store.state.audienceJoined)))
const adminSurface = computed(() => route.meta.adminSurface === true)
const transparentDisplay = computed(() => ['/wall', '/stage'].includes(route.path)
  && (route.query.background === 'transparent' || route.query.layout === 'transparent'))
const publicEventSurface = computed(() => route.meta.eventThemed === true && route.path !== '/moderate' && !adminSurface.value)
const sessionCode = computed(() => String(route.params.sessionCode || route.query.session || '').toUpperCase())
const sessionMatches = computed(() => route.meta.eventThemed === true && sessionCode.value && String(store.state.session.code || '').toUpperCase() === sessionCode.value)
const sessionTitle = computed(() => sessionMatches.value ? store.state.session.name : '')
const routeTitleKeys = { '/join': 'join.sessionCode', '/moderate': 'nav.moderate', '/wall': 'nav.wall', '/stage': 'nav.wall', '/speaker': 'nav.speaker', '/admin': 'nav.admin', '/legal/acceptable-use': 'legal.acceptableUseTitle', '/legal/privacy': 'legal.privacyTitle' }
const pageTitle = computed(() => adminSurface.value ? t('nav.admin') : sessionTitle.value || t(routeTitleKeys[route.path] || 'nav.audience'))

watch(() => route.query.lang, (lang) => syncLocale(lang), { immediate: true })
watch([pageTitle, () => language.locale], ([title]) => { document.title = title }, { immediate: true })

const links = computed(() => [
  { to: `/s/${store.state.session.code || 'join'}`, key: 'audience' },
  { to: '/moderate', key: 'moderate' },
  { to: '/wall', key: 'wall' },
  { to: `/speaker?session=${encodeURIComponent(store.state.session.code)}`, key: 'speaker' },
  { to: '/admin', key: 'admin' },
])

watch(
  [() => store.state.session.branding, () => publicEventSurface.value && sessionMatches.value],
  ([branding, enabled]) => applyBrandingStyles(branding, enabled),
  { deep: true, immediate: true },
)

watch(
  [() => store.state.session.branding?.colors, () => store.state.session.theme, () => publicEventSurface.value && sessionMatches.value],
  ([colors, theme, enabled]) => applyBrandPalette(colors, theme, enabled),
  { deep: true, immediate: true },
)

watch(
  [() => store.state.session.theme, () => publicEventSurface.value && sessionMatches.value],
  ([theme, eventThemed]) => applySessionTheme(eventThemed ? theme : null),
  { deep: true, immediate: true },
)

watch(
  adminSurface,
  (active) => document.documentElement.classList.toggle('admin-surface', active),
  { immediate: true },
)
watch(transparentDisplay, active => document.documentElement.classList.toggle('gather-display-transparent', active), { immediate: true })
onBeforeUnmount(() => document.documentElement.classList.remove('gather-display-transparent'))
</script>

<template>
  <div class="app" :class="{ 'app--bare': bare, 'app--admin': adminSurface, 'app--event': publicEventSurface, 'gather-event': publicEventSurface, 'app--demo': demoMode, 'app--audience': audienceSurface && store.state.audienceJoined && !store.state.accessBlocked }" @keydown="submitFormOnShortcut">
    <header v-if="!bare" class="topbar" :class="{ 'gather-titlebar': publicEventSurface }">
      <div class="session-titlebar">
        <EventBrand v-if="sessionMatches" :branding="store.state.session.branding" variant="audience" />
        <h1 :class="{ 'gather-titlebar__title': publicEventSurface }">{{ pageTitle }}</h1>
      </div>

      <nav v-if="demoMode" class="topbar__nav" aria-label="Demo surfaces">
        <RouterLink v-for="link in links" :key="link.key" :to="link.to">{{ t(`nav.${link.key}`) }}</RouterLink>
      </nav>

      <div v-if="demoMode" class="topbar__tools">
        <span v-if="demoMode" class="demo-pill"><Radio :size="14" /> {{ t('common.demo') }}</span>
      </div>
    </header>

    <main :class="bare ? 'bare-main' : 'app-main'">
      <RouterView :key="adminSurface ? 'administration' : route.path" />
    </main>

    <div class="toast-stack" role="status" aria-live="polite">
      <div v-for="item in store.state.toasts" :key="item.id" class="toast" :class="`toast--${item.tone}`">
        <Wifi v-if="item.tone === 'success'" :size="18" />
        <span>{{ item.message }}</span>
      </div>
    </div>
  </div>
</template>
